import React, { ReactNode } from 'react';

import { Flex, Text } from '@adsk/alloy-react-box';
import Button, { LinkButton } from '@adsk/alloy-react-button';

import { useTranslation } from '../i18n';
import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import { EntityWithId } from '../types/types';

export interface BaseProps<T> {
  plugin?: CommonReferencePickerPlugin | null;
  isSaving?: boolean;
  createPanelOpen?: boolean;
  clearSelection?: () => void;
  closeModal?: () => void;
  saveButtonText?: React.ReactNode;
  selectedEntities: T[];
}
export interface MultipleTrueProps<T> extends BaseProps<T> {
  multiple: true;
  onSave: (selectedEntities: T[]) => Promise<void> | void;
}

export interface MultipleFalseProps<T> extends BaseProps<T> {
  multiple?: false | undefined;
  onSave: (selectedEntity: T) => Promise<void> | void;
}

export type EntityPickerModalFooterProps<T = EntityWithId> =
  | MultipleTrueProps<T>
  | MultipleFalseProps<T>;

const renderSaveButtonText = (
  selectedEntitiesCount: number,
  saveButtonText?: ReactNode,
  plugin?: CommonReferencePickerPlugin | null,
  multiple?: boolean | undefined,
) => {
  if (saveButtonText) return saveButtonText;

  if (plugin?.AddRelationshipsButtonText) {
    return (
      <plugin.AddRelationshipsButtonText
        count={selectedEntitiesCount}
        multiple={!!multiple}
      />
    );
  }

  return '';
};

const EntityPickerModalFooter = <T extends EntityWithId>({
  plugin,
  multiple,
  isSaving,
  createPanelOpen,
  selectedEntities,
  clearSelection,
  closeModal,
  onSave,
  saveButtonText,
}: EntityPickerModalFooterProps<T>) => {
  const { t } = useTranslation();
  const selectedEntitiesCount = selectedEntities.length;

  return (
    <>
      <Flex alignItems="center">
        {multiple && selectedEntitiesCount > 0 && (
          <>
            {plugin && plugin?.SelectedCount ? (
              <Text color="charcoal700">
                <plugin.SelectedCount count={selectedEntitiesCount} />
              </Text>
            ) : null}
            <LinkButton
              onClick={clearSelection}
              disabled={isSaving || createPanelOpen}
              style={{
                marginLeft: 8,
              }}
            >
              {t('CLEAR')}
            </LinkButton>
          </>
        )}
      </Flex>
      <div>
        <Button
          data-testid="EntityPickerModalFooter--CancelButton"
          variant={Button.VARIANTS.TERTIARY}
          onClick={closeModal}
          disabled={isSaving}
          style={{
            marginRight: 8,
          }}
        >
          {t('CANCEL')}
        </Button>
        <Button
          data-testid="EntityPickerModalFooter--SaveButton"
          variant={Button.VARIANTS.PRIMARY}
          onClick={() => {
            if (multiple) {
              onSave?.(selectedEntities);
            } else {
              onSave?.(selectedEntities[0]);
            }
          }}
          disabled={isSaving || !selectedEntitiesCount || createPanelOpen}
          loading={isSaving}
        >
          {renderSaveButtonText(
            selectedEntitiesCount,
            saveButtonText,
            plugin,
            multiple,
          )}
        </Button>
      </div>
    </>
  );
};

export default EntityPickerModalFooter;
